import React, { useEffect, useState } from "react";
import schoolApi from "../../api/schoolApi";
import "./FeeReceipt.css";
import "react-toastify/dist/ReactToastify.css";
import { Col, Row } from "react-bootstrap";
import jwt_decode from "jwt-decode";
const parseSafe = (val) => {
  if (typeof val === "string") return parseInt(val.replace(/,/g, "")) || 0;
  return parseInt(val) || 0;
};
const FeeReceipt = React.forwardRef(({ receiptData, onReady }, receiptRefs) => {
  const [studentRecord, setStudentRecord] = useState();
  const [headRecords, setHeadRecords] = useState([]);
  const [depoMonths, setMonths] = useState([]);
  const [depositRecords, setdepositRecords] = useState([]);
  const [allMasterInstallments, setAllMasterInstallments] = useState([]);
  const [filterMonths, setFilterMonths] = useState([]);
  const [tenantCode, setTenantCode] = useState(null);
  const [overallPaidTotal, setOverallPaidTotal] = useState(0);

  useEffect(() => {
    if (receiptData) {
      setStudentRecord(null);
      setHeadRecords([]);
      setMonths([]);
      setdepositRecords([]);
      setAllMasterInstallments([]);
      setFilterMonths([]);
      setOverallPaidTotal(0);

      async function init() {
        try {
          sessionStorage.getItem("token");
          const user = jwt_decode(sessionStorage.getItem("token"));
          setTenantCode(user.tenantcode);
          let session = receiptData?.sessionid
            ? receiptData?.sessionid
            : receiptData?.session_id;

          // 1. First fetch the CURRENT receipt to get reliable session ID & deposit data
          const result = await schoolApi.fetchFeeDepositsByStudentId(
            receiptData?.student_addmission_id,
            null,
            receiptData?.deposit_id
          );

          if (!result || result.success === false) {
            return;
          }

          const depoRecordsResult = result?.result?.[0] || result?.[0];
          const sessionToUse = depoRecordsResult?.session_id || depoRecordsResult?.sessionid || session;

          // 2. Fetch ALL receipts for this student in this session for chronological history
          const allReceiptsResult = await schoolApi.fetchFeeDepositsByStudentId(
            receiptData?.student_addmission_id,
            sessionToUse
          );

          const allReceipts = (allReceiptsResult?.result || allReceiptsResult || []).filter(
            (r) => r && r.status !== "cancelled"
          );
          const depoRecords = depoRecordsResult;

          const allInstallmentsResult = await schoolApi.getallIntsallment(
            receiptData?.student_addmission_id,
            sessionToUse
          );

          const masterInstallments = allInstallmentsResult?.result || allInstallmentsResult || [];
          setAllMasterInstallments(masterInstallments);

          const getFirstReceiptNum = (str) => {
            if (!str) return 0;
            const match = String(str).match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          };

          const currentReceiptNum = getFirstReceiptNum(depoRecords?.receipt_number);
          const currentDepositId = depoRecords?.deposit_id || depoRecords?.id;
          const currentCreatedTime = depoRecords?.createddate ? new Date(depoRecords.createddate).getTime() : 0;

          // Helper to check if receipt 'r' occurred strictly prior to current receipt
          const isStrictlyPrior = (r) => {
            const rId = r.deposit_id || r.id;
            if (rId === currentDepositId) return false;
            const rCreatedTime = r.createddate ? new Date(r.createddate).getTime() : 0;
            if (rCreatedTime && currentCreatedTime && rCreatedTime !== currentCreatedTime) {
              return rCreatedTime < currentCreatedTime;
            }
            const rNum = getFirstReceiptNum(r.receipt_number);
            return rNum < currentReceiptNum;
          };

          // Helper to check if receipt 'r' occurred up to and including the current receipt
          const isUpToCurrent = (r) => {
            const rId = r.deposit_id || r.id;
            if (rId === currentDepositId) return true;
            return isStrictlyPrior(r);
          };

          const priorReceipts = allReceipts.filter(isStrictlyPrior);
          const receiptsUpToCurrent = allReceipts.filter(isUpToCurrent);

          // Calculate Total Fee Paid (Overall) = sum of all receipts up to and including this one
          const overallPaid = receiptsUpToCurrent.reduce((acc, r) => acc + parseSafe(r.amount || 0), 0);
          setOverallPaidTotal(overallPaid);

          const current_session = await schoolApi.getSessionById(depoRecords?.session_id || depoRecords?.sessionid);
          const filteredMonthsName = getMonthNamesBetweenDates(
            current_session?.result?.startdate,
            current_session?.result?.enddate
          );
          setFilterMonths(filteredMonthsName);

          // Filter and sort installments of this deposit
          if (depoRecords?.installments?.length > 0) {
            depoRecords.installments = depoRecords?.installments
              .map((installment) => {
                const notmonthlytype = installment.month.split(" to ");
                if (notmonthlytype.length === 1) {
                  return filteredMonthsName.includes(installment.month) ? installment : null;
                } else {
                  const [startMonth, endMonth] = notmonthlytype.map((m) => m.trim());
                  const startIndex = filteredMonthsName.indexOf(startMonth);
                  const endIndex = filteredMonthsName.indexOf(endMonth);
                  if (startIndex !== -1 && endIndex !== -1) {
                    const monthsInRange = filteredMonthsName.slice(startIndex, endIndex + 1);
                    return { ...installment, expandedMonths: monthsInRange };
                  } else {
                    return null;
                  }
                }
              })
              .filter(Boolean);

            depoRecords.installments.sort((a, b) => {
              const aFirstMonth = Array.isArray(a.expandedMonths) ? a.expandedMonths[0] : a.month;
              const bFirstMonth = Array.isArray(b.expandedMonths) ? b.expandedMonths[0] : b.month;
              return filteredMonthsName.indexOf(aFirstMonth) - filteredMonthsName.indexOf(bFirstMonth);
            });
          }

          // Prior payments & discounts per installment
          const instPriorPaymentMap = new Map();
          const instPriorDiscountMap = new Map();
          const instPriorTransportPaidMap = new Map();
          const instCurrentDiscountMap = new Map();

          // Sort prior receipts chronologically
          const sortedPriorReceipts = [...priorReceipts].sort((a, b) => {
            const aTime = a.createddate ? new Date(a.createddate).getTime() : 0;
            const bTime = b.createddate ? new Date(b.createddate).getTime() : 0;
            if (aTime && bTime && aTime !== bTime) return aTime - bTime;
            return getFirstReceiptNum(a.receipt_number) - getFirstReceiptNum(b.receipt_number);
          });

          (depoRecords?.installments || []).forEach((inst) => {
            // Total manual discounts recorded across all receipts for this installment
            const allManualDiscountsForInst = allReceipts
              .filter(
                (r) =>
                  (r.installments || []).some((i) => i.id === inst.id) ||
                  (r.payment_breakdown || []).some((pb) => pb.installment_id === inst.id)
              )
              .reduce((sum, r) => sum + parseSafe(r.discount || 0), 0);

            const totalInstDiscountInDB = parseSafe(inst.installment_discount || 0);

            // Base preset discount for this installment is derived from installment_discount in DB minus subsequent manual discounts
            const basePresetDiscount = Math.max(
              0,
              totalInstDiscountInDB - allManualDiscountsForInst
            );

            let priorPaid = 0;
            let priorDisc = 0;
            let hasActuallyBeenPaidPrior = false;

            sortedPriorReceipts.forEach((pr) => {
              const breakdown = pr.payment_breakdown || [];
              const pb = breakdown.find((p) => p.installment_id === inst.id);
              const prHasInst = (pr.installments || []).some((i) => i.id === inst.id) || !!pb;

              if (prHasInst) {
                let paidInThisPr = 0;
                if (pb) {
                  paidInThisPr = parseSafe(pb.amount || 0);
                } else {
                  // Distribute prior receipt payment waterfall across its installments
                  let prRemTuition = Math.max(0, parseSafe(pr.amount || 0) - parseSafe(pr.late_fee || 0));
                  const prSortedInsts = [...(pr.installments || [])].sort((a, b) => {
                    const aOrder = a.orderno || 0;
                    const bOrder = b.orderno || 0;
                    return aOrder - bOrder;
                  });

                  for (const pInst of prSortedInsts) {
                    const pAmt = parseSafe(pInst.amount || pInst.installment_amount || 0);
                    const pTransport = parseSafe(pInst.transport_fee || 0);
                    const pDisc = parseSafe(pInst.installment_discount || 0);
                    const pPreviousDue = parseSafe(pInst.previous_due || pInst.installment_due_amount || 0);
                    const pDue = Math.max(0, pAmt + pTransport + pPreviousDue - pDisc);
                    const pPay = Math.min(prRemTuition, pDue);
                    prRemTuition = Math.max(0, prRemTuition - pPay);
                    if (pInst.id === inst.id) {
                      paidInThisPr = pPay;
                      break;
                    }
                  }
                }

                const numPrInsts = (pr.installments || []).length || 1;
                const prManualDisc = parseSafe(pr.discount || 0) / numPrInsts;

                if (paidInThisPr > 0 || prManualDisc > 0) {
                  priorPaid += paidInThisPr;
                  if (!hasActuallyBeenPaidPrior) {
                    priorDisc += basePresetDiscount + prManualDisc;
                    hasActuallyBeenPaidPrior = true;
                  } else if (prManualDisc > 0) {
                    priorDisc += prManualDisc;
                  }
                }
              }
            });

            instPriorPaymentMap.set(inst.id, priorPaid);
            instPriorDiscountMap.set(inst.id, priorDisc);
            const priorTransportPaid = Math.min(
              parseSafe(inst.transport_fee || 0),
              Math.max(0, priorPaid - Math.max(0,
                parseSafe(inst.installment_amount || 0) +
                parseSafe(inst.installment_due_amount || inst.previous_due || 0) -
                priorDisc
              ))
            );
            instPriorTransportPaidMap.set(inst.id, priorTransportPaid);

            // Discount for THIS current receipt on this installment
            const numCurrentInsts = (depoRecords?.installments || []).length || 1;
            const currentManualDisc = parseSafe(depoRecords?.discount || 0) / numCurrentInsts;

            let thisReceiptInstDisc = 0;
            if (!hasActuallyBeenPaidPrior) {
              thisReceiptInstDisc = basePresetDiscount + currentManualDisc;
            } else {
              thisReceiptInstDisc = currentManualDisc;
            }

            instCurrentDiscountMap.set(inst.id, thisReceiptInstDisc);
          });

          // Prior late fee paid per installment
          const instPriorLateFeeMap = new Map();
          (depoRecords?.installments || []).forEach((inst) => {
            let priorLatePaid = 0;
            sortedPriorReceipts.forEach((pr) => {
              if (
                (pr.installments || []).some((i) => i.id === inst.id) ||
                (pr.payment_breakdown || []).some((pb) => pb.installment_id === inst.id)
              ) {
                priorLatePaid += parseSafe(pr.late_fee || 0);
              }
            });
            instPriorLateFeeMap.set(inst.id, priorLatePaid);
          });

          // Total late fee liability on current deposit's installments
          let totalInstLateFeeLiability = 0;
          let totalInstPriorLatePaid = 0;
          (depoRecords?.installments || []).forEach((inst) => {
            const masterInst = (allMasterInstallments || []).find(
              (m) => m.id === inst.id || m.month?.trim().toLowerCase() === inst.month?.trim().toLowerCase()
            );
            const instLate = Math.max(
              parseSafe(inst.pushed_late_fee || 0),
              parseSafe(inst.late_fee || 0),
              parseSafe(inst.late_fee_paid || 0),
              parseSafe(masterInst?.pushed_late_fee || 0),
              parseSafe(masterInst?.late_fee || 0),
              parseSafe(masterInst?.late_fee_paid || 0)
            );
            totalInstLateFeeLiability += instLate;
            totalInstPriorLatePaid += instPriorLateFeeMap.get(inst.id) || 0;
          });

          const remainingInstLateFee = Math.max(0, totalInstLateFeeLiability - totalInstPriorLatePaid);

          // Determine current receipt late fee
          let currentReceiptLateFee = parseSafe(depoRecords?.late_fee || 0);
          const currentReceiptAmount = parseSafe(depoRecords?.amount || 0);

          if (currentReceiptLateFee === 0) {
            let totalInstTuitionRemaining = 0;
            (depoRecords?.installments || []).forEach((inst) => {
              const headsSum = (inst.heads || []).reduce((sum, h) => sum + parseSafe(h.head_amount), 0);
              const transFee = parseSafe(inst.transport_fee || 0);
              const prevDue = parseSafe(inst.installment_due_amount || inst.previous_due || 0);
              const instOrig = headsSum + transFee + prevDue;
              const priorP = instPriorPaymentMap.get(inst.id) || 0;
              const priorD = instPriorDiscountMap.get(inst.id) || 0;
              const thisD = instCurrentDiscountMap.get(inst.id) || 0;
              totalInstTuitionRemaining += Math.max(0, instOrig - priorD - thisD - priorP);
            });

            if (currentReceiptAmount > totalInstTuitionRemaining) {
              const diffLate = currentReceiptAmount - totalInstTuitionRemaining;
              currentReceiptLateFee = remainingInstLateFee > 0 ? Math.min(remainingInstLateFee, diffLate) : diffLate;
            }
          }

          const currentReceiptDiscount = Array.from(instCurrentDiscountMap.values()).reduce((sum, d) => sum + d, 0);
          const currentReceiptTuitionPaid = Math.max(0, currentReceiptAmount - currentReceiptLateFee);

          let headArray = [];
          let months = [];
          let transportMonths = [];
          let totalTransportfeeDue = 0;
          let totalTransportfeePaid = 0;

          // Track remaining tuition payment to distribute across heads
          let remainingTuitionToDistribute = currentReceiptTuitionPaid;
          let remainingDiscountToDistribute = currentReceiptDiscount;

          // Count frequency of each discount across heads to distinguish head-specific vs general concessions
          const discountHeadCount = {};
          (depoRecords?.installments || []).forEach((inst) => {
            (inst.heads || []).forEach((h) => {
              const concessions = h.concession || h.discount || [];
              concessions.forEach((c) => {
                const key = c.discountid || c.discount_name;
                if (key) {
                  discountHeadCount[key] = (discountHeadCount[key] || 0) + 1;
                }
              });
            });
          });

          (depoRecords?.installments || []).forEach((inst) => {
            if (!months.some((m) => m.month === inst.month)) {
              months.push({ month: inst.month });
            }

            const instPriorPaid = instPriorPaymentMap.get(inst.id) || 0;
            let instPriorDiscount = instPriorDiscountMap.get(inst.id) || 0;

            // 1. Deduct head-specific prior concessions from their respective heads first
            const headsWithSpecificDeduction = (inst.heads || []).map((head) => {
              const origAmt = parseSafe(head.head_amount);
              let specificConcessionSum = 0;
              const concessions = head.concession || head.discount || [];
              concessions.forEach((c) => {
                const key = c.discountid || c.discount_name;
                if (key && discountHeadCount[key] === 1) {
                  specificConcessionSum += parseSafe(c.concession_value || c.discount_amount || 0);
                }
              });

              const specificDeduction = Math.min(origAmt, Math.min(instPriorDiscount, specificConcessionSum));
              instPriorDiscount = Math.max(0, instPriorDiscount - specificDeduction);
              const remainingAmtAfterSpecific = Math.max(0, origAmt - specificDeduction);

              return {
                ...head,
                origAmt,
                remainingAmtAfterSpecific,
              };
            });

            // 2. Deduct remaining general prior deductions (prior payments + general prior discounts) in priority (Tuition Fee first)
            let remainingGeneralPriorDeduction = instPriorPaid + instPriorDiscount;

            const instSortedHeads = [...headsWithSpecificDeduction].sort((a, b) => {
              const isATuition = (a.headname || "").toLowerCase().includes("tuition");
              const isBTuition = (b.headname || "").toLowerCase().includes("tuition");
              if (isATuition && !isBTuition) return -1;
              if (!isATuition && isBTuition) return 1;
              return 0;
            });

            instSortedHeads.forEach((head) => {
              const headDeduction = Math.min(head.remainingAmtAfterSpecific, remainingGeneralPriorDeduction);
              remainingGeneralPriorDeduction = Math.max(0, remainingGeneralPriorDeduction - headDeduction);
              const remainingHeadDue = Math.max(0, head.remainingAmtAfterSpecific - headDeduction);

              if (remainingHeadDue > 0) {
                const existingHead = headArray.find((prev) => prev.headid === head.headid);
                if (existingHead) {
                  existingHead.head_amount += remainingHeadDue;
                  if (!existingHead.month.includes(inst.month)) {
                    existingHead.month.push(inst.month);
                  }
                } else {
                  headArray.push({
                    ...head,
                    head_amount: remainingHeadDue,
                    month: [inst.month],
                  });
                }
              }
            });

            const instTransportFee = parseSafe(inst.transport_fee || 0);
            const priorTransportPaid = instPriorTransportPaidMap.get(inst.id) || 0;
            const remainingTransportDue = Math.max(0, instTransportFee - priorTransportPaid);
            const instTransportPaidInThisReceipt = Math.max(
              0,
              parseSafe(inst.transport_fee_paid || 0) - priorTransportPaid
            );

            if (remainingTransportDue > 0 || instTransportPaidInThisReceipt > 0) {
              totalTransportfeeDue += remainingTransportDue;
              totalTransportfeePaid += instTransportPaidInThisReceipt;
              if (!transportMonths.includes(inst.month)) {
                transportMonths.push(inst.month);
              }
            }
          });

          // Add Transport Fee if present
          if (totalTransportfeeDue > 0 || totalTransportfeePaid > 0) {
            if (filteredMonthsName?.length > 0) {
              transportMonths.sort((a, b) => filteredMonthsName.indexOf(a) - filteredMonthsName.indexOf(b));
            }
            headArray.push({
              headname: "Transport Fee",
              head_amount: totalTransportfeeDue,
              month: transportMonths.length > 0 ? transportMonths : months.map((m) => m.month),
              displayDiscount: 0,
            });
          }

          // Add Late Fee (Charged) row ONLY if late fee was charged/paid in THIS receipt
          if (currentReceiptLateFee > 0) {

            // const lateFeeCharged =Math.max(currentReceiptLateFee, totalInstLateFeeLiability-totalInstPriorLatePaid);

            headArray.push({
              headname: "Late Fee (Charged)",
              head_amount: currentReceiptLateFee,
              // head_amount: lateFeeCharged,
              month: months.map((m) => m.month),
              paidAmountOverride: currentReceiptLateFee,
              displayDiscount: 0,
            });
          }



          // 1. Allocate Head-Specific Concessions first (e.g. Admission fee discount)
          headArray.forEach((item) => {
            if (item.paidAmountOverride !== undefined) {
              item.displayDiscount = 0;
            } else {
              let specificConcessionSum = 0;
              const concessions = item.concession || item.discount || [];
              concessions.forEach((c) => {
                const key = c.discountid || c.discount_name;
                if (key && discountHeadCount[key] === 1) {
                  specificConcessionSum += parseSafe(c.concession_value || c.discount_amount || 0);
                }
              });

              const allocatedSpecific = Math.min(
                item.head_amount,
                Math.min(remainingDiscountToDistribute, specificConcessionSum)
              );
              item.displayDiscount = allocatedSpecific;
              remainingDiscountToDistribute = Math.max(0, remainingDiscountToDistribute - allocatedSpecific);
            }
          });

          // 2. Allocate remaining general discounts in priority (Tuition Fee first, then other heads, then Transport)
          const discountableHeads = headArray.filter((h) => h.headname !== "Late Fee (Charged)");
          discountableHeads.sort((a, b) => {
            const isATuition = (a.headname || "").toLowerCase().includes("tuition");
            const isBTuition = (b.headname || "").toLowerCase().includes("tuition");
            if (isATuition && !isBTuition) return -1;
            if (!isATuition && isBTuition) return 1;
            const isATransport = (a.headname || "").toLowerCase().includes("transport");
            const isBTransport = (b.headname || "").toLowerCase().includes("transport");
            if (!isATransport && isBTransport) return -1;
            if (isATransport && !isBTransport) return 1;
            return 0;
          });

          discountableHeads.forEach((item) => {
            if (remainingDiscountToDistribute > 0) {
              const spaceAvailable = Math.max(0, item.head_amount - (item.displayDiscount || 0));
              const additionalDiscount = Math.min(spaceAvailable, remainingDiscountToDistribute);
              item.displayDiscount = (item.displayDiscount || 0) + additionalDiscount;
              remainingDiscountToDistribute = Math.max(0, remainingDiscountToDistribute - additionalDiscount);
            }
          });

          // 3. Allocate payments in priority (Tuition Fee first)
          headArray.forEach((item) => {
            if (item.paidAmountOverride !== undefined) {
              item.paidInThisReceipt = item.paidAmountOverride;
            } else {
              const netHeadDue = Math.max(0, item.head_amount - (item.displayDiscount || 0));
              const itemPaid = Math.min(netHeadDue, remainingTuitionToDistribute);
              remainingTuitionToDistribute = Math.max(0, remainingTuitionToDistribute - itemPaid);
              item.paidInThisReceipt = itemPaid;
            }
          });

          setMonths(months);
          setStudentRecord(depoRecords);
          setHeadRecords(headArray.filter((res) => res.head_amount > 0));
        } catch (error) {
          console.error("Error in FeeReceipt init:", error);
        }
      }
      init();
    }
  }, [receiptData]);

  useEffect(() => {
    if (studentRecord && onReady) {
      onReady();
    }
  }, [studentRecord, onReady]);

  function getMonthNamesBetweenDates(start, end) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const result = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const monthName = monthNames[currentDate.getMonth()];
      if (!result.includes(monthName)) {
        result.push(monthName);
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return result;
  }

  const convertNumberToWords = (num) => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const g = ["", "Thousand", "Million", "Billion", "Trillion"];
    if ((num = num.toString()).length > 15) return "too large";
    let n = ("000000000000000" + num).substr(-15).match(/.{3}/g);
    let str = "";
    for (let i = 0; i < n.length; i++) {
      let hundreds = parseInt(n[i][0]);
      let tens = parseInt(n[i][1]);
      let units = parseInt(n[i][2]);
      let word = "";
      if (hundreds > 0) {
        word += a[hundreds] + " hundred ";
      }
      if (tens > 1) {
        word += b[tens] + " " + a[units];
      } else {
        word += a[10 * tens + units];
      }
      if (word.trim()) {
        str += word + " " + g[n.length - 1 - i] + " ";
      }
    }
    return str.replace(/\s+/g, " ").trim();
  };

  const totalFees = headRecords?.reduce((acc, item) => acc + parseSafe(item?.head_amount), 0);
  const totalConcessionAmount = headRecords?.reduce((acc, item) => acc + parseSafe(item?.displayDiscount || 0), 0);
  const netPayable = Math.max(0, totalFees - totalConcessionAmount);
  const currentPaid = parseSafe(studentRecord?.amount || 0);
  const receiptBalance = Math.max(0, netPayable - currentPaid);



  return (
    <>
      <div className="fee-receipt-container-receipt">
        {studentRecord ? (
          <div
            id="reportContent"
            ref={receiptRefs}
            className="fee-receipt-receipt"
          >
            {["Office Copy", "Parent Copy"].map((copyType, idx) => (
              <div key={idx} className="receipt-copy-receipt">
                <header className="header-receipt">
                  <Row>
                    <Col lg={3}>
                      <img
                        src={`/${tenantCode}-sidebar-logo.png`}
                        style={{ width: "100px", height: "100px" }}
                        alt=""
                      />
                    </Col>
                    <Col lg={6}>
                      <h1>{studentRecord.school_name}</h1>
                      <p>
                        {studentRecord.school_address},<br />
                        State - {studentRecord.state}, {studentRecord.country},
                        <br />
                        Pin code - {studentRecord.pincode}
                      </p>
                    </Col>
                    <Col lg={3}>
                      <p className="float-end">{copyType}</p>
                    </Col>
                  </Row>
                </header>
                { }
                <h4 class="text-center border-dark border-top border-bottom container-fluid">
                  Fee Receipt ({studentRecord.session})
                </h4>
                { }
                <div className="receipt-info-receipt">
                  <div className="info-left-receipt">
                    <p>
                      <strong>Receipt No :</strong>{" "}
                      {studentRecord.receipt_number}
                    </p>
                    <p>
                      <strong>Student Name :</strong>{" "}
                      {studentRecord.studentname}
                    </p>
                    <p>
                      <strong>Registration No :</strong>{" "}
                      {studentRecord.registration_id}
                    </p>
                    <p>
                      <strong>Mobile No :</strong> {studentRecord.phone}
                    </p>
                  </div>
                  <div className="info-right-receipt">
                    <p>
                      <strong>Receipt Date :</strong>{" "}
                      {studentRecord.payment_date}
                    </p>
                    <p>
                      <strong>Parent's Name :</strong>{" "}
                      {studentRecord.parent_name}
                    </p>
                    <p>
                      <strong>Class :</strong> {studentRecord.classname} (
                      {studentRecord.sectionname})
                    </p>
                    <p>
                      <strong>Parent's Contact :</strong>{" "}
                      {studentRecord.parentphone}
                    </p>
                    <p>
                      <strong>Total Fee Paid (Overall) :</strong>{" "}
                      {overallPaidTotal}
                    </p>
                  </div>
                </div>
                <table className="table-receipt">
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>Particulars</th>
                      <th>Due Month</th>
                      <th>Due Amount</th>
                      {totalConcessionAmount > 0 && <th>Concession Amount</th>}
                      <th>Paid Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headRecords.map((item, index) => (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>{item.headname}</td>
                        <td className="text-center">
                          {(Array.isArray(item.month) ? item.month : []).map((month, idx) => (
                            <div key={idx}>{month}</div>
                          ))}
                        </td>
                        <td className="amount text-right">{item.head_amount}</td>
                        {totalConcessionAmount > 0 && (
                          <td className="amount text-right">{item.displayDiscount || 0}</td>
                        )}
                        <td className="amount text-right">{item.paidInThisReceipt}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                        <strong>Total Amount (Gross) (in Rs):</strong>
                      </td>
                      <td className="amount text-right">
                        <strong>{totalFees}</strong>
                      </td>
                    </tr>
                    {totalConcessionAmount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-right">
                          <strong>Total Concession (in Rs):</strong>
                        </td>
                        <td className="amount text-right">
                          <strong>{totalConcessionAmount}</strong>
                        </td>
                      </tr>
                    )}
                    {totalConcessionAmount > 0 && (
                      <tr style={{ backgroundColor: '#f0f0f0' }}>
                        <td colSpan="5" className="text-right">
                          <strong>Net Payable Amount (in Rs):</strong>
                        </td>
                        <td className="amount text-right">
                          <strong>{totalFees - totalConcessionAmount}</strong>
                        </td>
                      </tr>
                    )}
                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                      <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                        <strong>Current Paid (in Rs):</strong>
                      </td>
                      <td className="amount">
                        <strong>{studentRecord.amount}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                        <strong>Due Amount (Balance):</strong>
                      </td>
                      <td className="amount">
                        <strong>
                          {receiptBalance}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={totalConcessionAmount > 0 ? "6" : "5"} className="text-right">
                        <strong className="text-capitalize">
                          Amount In Words: Rupees{" "}
                          {convertNumberToWords(studentRecord.amount)} Only.
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={totalConcessionAmount > 0 ? "6" : "5"} className="text-right">
                        <strong>Payment Mode:</strong>{" "}
                        {studentRecord.payment_method} |
                        {studentRecord.transaction_no ? (
                          <>
                            <strong>Cheque No. / Transaction Id:</strong>{" "}
                            {studentRecord.transaction_no} |
                          </>
                        ) : null}
                        <strong>Cheque Date / Transaction Date:</strong>{" "}
                        {studentRecord.transaction_date}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                <div className="remarks-receipt">
                  <p>
                    <strong>Remark:</strong> {studentRecord.remark}
                  </p>
                </div>
                <div className="footer-receipt">
                  <p>
                    Generated by {studentRecord.generated_by} on{" "}
                    {studentRecord.generated_date}
                  </p>
                  <p>
                    <strong>NOTE:</strong> FEE IS NOT REFUNDABLE (EXCEPT CAUTION
                    FEE)
                  </p>
                </div>
              </div>
            ))}
            <div className="dotted-line"></div>
          </div>
        ) : (
          <p>No Receipt Found</p>
        )}
      </div>
    </>
  );
});

export default FeeReceipt;
